import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  useFieldArray,
  type Control,
  type FieldArrayWithId
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Button, IconButton } from 'react-native-paper';
import type { SortableGridRenderItem } from 'react-native-sortables';
import Sortable from 'react-native-sortables';
import type { DragEndParams } from 'react-native-sortables/dist/typescript/types';

import { AddIcon, DeleteIcon, DragIcon } from '@/assets';
import {
  MAX_OPTION_COUNT,
  MIN_OPTION_COUNT
} from '@/features/post-editor/constants/poll';
import type { PollCreate } from '@/features/post-editor/types/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FormGroup } from '@/shared/components/forms/FormGroup';
import { FormTextInput } from '@/shared/components/forms/FormTextInput';
import { isWeb } from '@/shared/utils/platform';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    inputContainer: {
      flex: 1,
      gap: 8,
      flexDirection: 'row',
      alignItems: 'flex-start'
    },
    inputWrapper: { flex: 1 },
    input: { backgroundColor: theme.colors.surfaceContainerLow },
    inputButtons: {
      paddingTop: 8,
      flexDirection: 'row'
    },
    addButton: { flex: 1, alignItems: 'flex-end' }
  });

type Props = {
  control: Control<PollCreate>;
  maxLength?: number;
};

export const PollOptionsFrom: FC<Props> = ({ control, maxLength }) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);

  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'components.poll.form'
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'optionTexts'
  });

  const canAddOption = fields.length < MAX_OPTION_COUNT;
  const [autoFocusIndex, setAutoFocusIndex] = useState<number>();

  const handleAdd = useCallback(() => {
    if (fields.length < MAX_OPTION_COUNT) {
      append({ optionText: '' });
      setAutoFocusIndex(fields.length);
    }
  }, [append, fields.length]);

  const handleDelete = useCallback(
    (index: number) => {
      if (fields.length <= MIN_OPTION_COUNT) return;
      remove(index);
    },
    [fields.length, remove]
  );

  const handleMove = useCallback(
    ({ fromIndex, toIndex }: DragEndParams) => move(fromIndex, toIndex),
    [move]
  );

  const renderItem = useCallback<
    SortableGridRenderItem<FieldArrayWithId<PollCreate, 'optionTexts', 'id'>>
  >(
    ({ item, index }) => (
      <View style={styles.inputContainer}>
        <FormTextInput
          key={item.id}
          control={control}
          containerStyle={styles.inputWrapper}
          style={styles.input}
          name={`optionTexts.${index}.optionText`}
          mode="outlined"
          label={t('option.title', { index: index + 1 })}
          multiline={!isWeb}
          maxLength={maxLength}
          // oxlint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocusIndex === index}
        />
        <View style={styles.inputButtons}>
          {fields.length > MIN_OPTION_COUNT && (
            <IconButton
              containerColor={theme.colors.surfaceContainerLow}
              onPress={() => handleDelete(index)}
              style={{ marginHorizontal: 0 }}
              icon={({ size }) => (
                <DeleteIcon
                  width={size}
                  height={size}
                  fill={theme.colors.error}
                />
              )}
            />
          )}
          {fields.length > 1 && (
            <Sortable.Handle>
              <IconButton
                containerColor={theme.colors.surfaceContainerLow}
                icon={({ color, size }) => (
                  <DragIcon
                    width={size}
                    height={size}
                    fill={color}
                    style={Platform.select({ web: { cursor: 'pointer' } })}
                  />
                )}
                style={{ marginHorizontal: 0 }}
              />
            </Sortable.Handle>
          )}
        </View>
      </View>
    ),
    [
      autoFocusIndex,
      control,
      fields.length,
      handleDelete,
      maxLength,
      styles.input,
      styles.inputWrapper,
      styles.inputButtons,
      styles.inputContainer,
      t,
      theme.colors.error,
      theme.colors.surfaceContainerLow
    ]
  );

  return (
    <FormGroup label={t('options', { count: fields.length })}>
      <Sortable.Grid
        data={fields}
        renderItem={renderItem}
        rowGap={16}
        customHandle
        onDragEnd={handleMove}
        activeItemScale={1.0125}
        overDrag="vertical"
        activeItemShadowOpacity={0.25}
        dragActivationDelay={100}
        activationAnimationDuration={100}
      />

      <View style={styles.addButton}>
        <Button
          disabled={!canAddOption}
          compact
          icon={({ color, size }) => (
            <AddIcon width={size} height={size} fill={color} />
          )}
          mode="text"
          onPress={handleAdd}
        >
          {t('addOption')}
        </Button>
      </View>
    </FormGroup>
  );
};
